import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LuChevronRight, LuLaptop, LuSmartphone } from 'react-icons/lu';

import { logEvent } from '~/config/firebase.config';
import { useSendTvLink } from '~/pages/Onboarding/useSendTvLink';
import { consoleAddress } from '~/utils/consoleAddress';

import styles from './PhoneHome.module.css';

/**
 * Accueil de la console ouverte sur un TÉLÉPHONE (bar en mode animé).
 *
 * Avant, le téléphone affichait la page de jeu elle-même : elle se déclarait « en
 * direct » auprès de la télécommande, le nouveau venu lançait un jeu… sur son
 * propre téléphone, et personne ne voyait rien (Le Cuba, 20/10/2026 ; 80
 * établissements ont ouvert cette page sur téléphone en 30 j, dont 25 jamais sur
 * ordinateur). V2 (retours de Victorien) : une idée, une action — on ne parle ni de
 * « TV » ni d'« ordinateur branché à la TV » (ça fait peur aux moins à l'aise).
 */
export function PhoneHome({ clientID, email, onShowTv }: { clientID: string; email: string; onShowTv: () => void }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { state, send, canSend } = useSendTvLink(email, clientID, 'phone_home');

    useEffect(() => {
        logEvent('phone_home_shown', clientID, { clientID });
    }, [clientID]);

    const goAnimator = () => {
        logEvent('phone_home_animator', clientID, { clientID });
        navigate(`/animator/${clientID}`);
    };

    const showTv = () => {
        logEvent('phone_home_show_tv', clientID, { clientID });
        onShowTv();
    };

    return (
        <div className={styles.page}>
            <main className={styles.content}>
                <LuLaptop className={styles.icon} aria-hidden="true" />
                <h1 className={styles.title}>{t('phoneHome.title')}</h1>
                <p className={styles.subtitle}>{t('phoneHome.subtitle')}</p>

                {state === 'sent' ? (
                    <div className={styles.sentBlock} role="status">
                        <p className={styles.sent}>{t('phoneHome.sent', { email })}</p>
                        <p className={styles.hint}>{t('phoneHome.spamHint')}</p>
                    </div>
                ) : (
                    canSend && (
                        <button type="button" className={styles.primary} onClick={send} disabled={state === 'sending'}>
                            {state === 'sending' ? t('phoneHome.sending') : t('phoneHome.send')}
                        </button>
                    )
                )}
                {state === 'error' && (
                    <p className={styles.error} role="alert">
                        {t('phoneHome.error')}
                    </p>
                )}
                <p className={styles.orType}>
                    {t('phoneHome.orType')} <span className={styles.address}>{consoleAddress()}</span>
                </p>

                <button type="button" className={styles.row} onClick={goAnimator}>
                    <LuSmartphone className={styles.rowIcon} aria-hidden="true" />
                    <span className={styles.rowText}>
                        <span className={styles.rowTitle}>{t('phoneHome.animator.title')}</span>
                        <span className={styles.rowSub}>{t('phoneHome.animator.text')}</span>
                    </span>
                    <LuChevronRight className={styles.rowChevron} aria-hidden="true" />
                </button>
            </main>

            <footer className={styles.footer}>
                <button type="button" className={styles.showTv} onClick={showTv}>
                    {t('phoneHome.showTv')}
                </button>
            </footer>
        </div>
    );
}
